/**
 * SSE (Server-Sent Events) client for pipeline progress streaming.
 */

import type { ProgressEvent } from "../types";
import { getSessionToken } from "./client";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";

export interface SSECallbacks {
  onEvent: (event: ProgressEvent, streamId?: string) => void;
  onError?: (error: Event) => void;
  onComplete?: () => void;
}

export interface SSEOptions {
  lastEventId?: string | null;
}

/**
 * Subscribe to a pipeline run's SSE event stream.
 * Returns a cleanup function to close the connection.
 */
export function subscribeToRun(runId: string, callbacks: SSECallbacks, options?: SSEOptions): () => void {
  const token = getSessionToken();
  const baseUrl = `${API_BASE}/runs/${runId}/events`;
  const params = new URLSearchParams();
  if (token) params.set("token", token);
  if (options?.lastEventId) params.set("lastEventId", options.lastEventId);
  const url = params.size > 0 ? `${baseUrl}?${params.toString()}` : baseUrl;
  const eventSource = new EventSource(url, { withCredentials: true });

  eventSource.onopen = () => {
    console.log("[SSE] Connection opened for run:", runId);
  };

  // The backend emits UNNAMED SSE events — `server/events.py` `to_sse()
  // produces `id: <n>\ndata: <json>\n\n` with no `event:` line.
  // EventSource.onmessage fires for all unnamed events and populates
  // `e.lastEventId` correctly, so this single handler captures
  // everything. Do NOT switch to per-type addEventListener listeners;
  // named events would require an explicit list and would bypass
  // the lastEventId capture for reconnect.
  eventSource.onmessage = (e: MessageEvent) => {
    try {
      const data: ProgressEvent = JSON.parse(e.data);
      callbacks.onEvent(data, e.lastEventId || undefined);

      // heartbeat is a keepalive — it resets the watchdog clock in
      // useVariantCRun but does NOT close the EventSource.
      if (
        data.event_type === "pipeline_complete" ||
        data.event_type === "error" ||
        data.event_type === "cancelled" ||
        data.event_type === "paused"
      ) {
        eventSource.close();
        callbacks.onComplete?.();
      }
    } catch {
      // Ignore unparseable messages (e.g. keepalive)
    }
  };

  eventSource.onerror = (e) => {
    // EventSource auto-reconnects — only notify on permanent closure
    if (eventSource.readyState === EventSource.CLOSED) {
      console.error("[SSE] Connection permanently closed");
      callbacks.onError?.(e);
    } else {
      console.warn("[SSE] Connection interrupted, auto-reconnecting...");
    }
  };

  return () => eventSource.close();
}
